import { useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { useBrand } from "@/config/brands";

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

export const stateAbbreviations: Record<string, string> = {
  "Alabama": "AL", "Alaska": "AK", "Arizona": "AZ", "Arkansas": "AR", "California": "CA",
  "Colorado": "CO", "Connecticut": "CT", "Delaware": "DE", "Florida": "FL", "Georgia": "GA",
  "Hawaii": "HI", "Idaho": "ID", "Illinois": "IL", "Indiana": "IN", "Iowa": "IA",
  "Kansas": "KS", "Kentucky": "KY", "Louisiana": "LA", "Maine": "ME", "Maryland": "MD",
  "Massachusetts": "MA", "Michigan": "MI", "Minnesota": "MN", "Mississippi": "MS", "Missouri": "MO",
  "Montana": "MT", "Nebraska": "NE", "Nevada": "NV", "New Hampshire": "NH", "New Jersey": "NJ",
  "New Mexico": "NM", "New York": "NY", "North Carolina": "NC", "North Dakota": "ND", "Ohio": "OH",
  "Oklahoma": "OK", "Oregon": "OR", "Pennsylvania": "PA", "Rhode Island": "RI", "South Carolina": "SC",
  "South Dakota": "SD", "Tennessee": "TN", "Texas": "TX", "Utah": "UT", "Vermont": "VT",
  "Virginia": "VA", "Washington": "WA", "West Virginia": "WV", "Wisconsin": "WI", "Wyoming": "WY"
};

interface USAMapNavigatorProps {
  onStateClick: (stateCode: string) => void;
  selectedState?: string;
}

export const USAMapNavigator = ({ onStateClick, selectedState }: USAMapNavigatorProps) => {
  const [hoveredState, setHoveredState] = useState<string | null>(null);
  const brand = useBrand();
  const isAhp = brand.design === 'civic';

  const hasSelection = selectedState && selectedState !== 'USA';

  return (
    <div className="w-[280px]" style={{ maxHeight: '120px' }}>
      {/* SVG filter for drop shadow on selected state */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <filter id="state-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="3" result="blur" />
            <feFlood floodColor={isAhp ? 'var(--civic-gold)' : '#00ff41'} floodOpacity="0.6" result="color" />
            <feComposite in="color" in2="blur" operator="in" result="shadow" />
            <feMerge>
              <feMergeNode in="shadow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      </svg>

      <ComposableMap projection="geoAlbersUsa" className="w-full h-auto" style={{ maxHeight: '120px' }}>
        <Geographies geography={geoUrl}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const stateName = geo.properties.name;
              const stateCode = stateAbbreviations[stateName];
              const isHovered = hoveredState === stateCode;
              const isSelected = hasSelection && stateCode === selectedState;
              const isDimmed = hasSelection && !isSelected && !isHovered;

              // Colors
              let fill: string;
              let strokeColor: string;
              let strokeWidth: number;
              let opacity: number;
              let filter: string | undefined;

              if (isSelected) {
                fill = isAhp ? 'var(--civic-gold)' : 'hsl(var(--primary))';
                strokeColor = isAhp ? 'var(--civic-cream)' : '#fff';
                strokeWidth = 1.5;
                opacity = 1;
                filter = 'url(#state-glow)';
              } else if (isHovered) {
                fill = isAhp ? 'var(--civic-red)' : 'hsl(var(--primary))';
                strokeColor = isAhp ? 'var(--civic-cream)' : 'hsl(var(--border))';
                strokeWidth = 1;
                opacity = 1;
              } else if (isDimmed) {
                fill = isAhp ? 'var(--civic-navy-deep)' : 'hsl(var(--muted-foreground))';
                strokeColor = isAhp ? 'rgba(var(--civic-cream-rgb),0.08)' : 'hsl(var(--border))';
                strokeWidth = 0.3;
                opacity = 0.35;
              } else {
                fill = isAhp ? '#2a5a8c' : 'hsl(var(--muted-foreground))';
                strokeColor = isAhp ? 'rgba(var(--civic-cream-rgb),0.15)' : 'hsl(var(--border))';
                strokeWidth = 0.5;
                opacity = 0.6;
              }

              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  onMouseEnter={() => setHoveredState(stateCode)}
                  onMouseLeave={() => setHoveredState(null)}
                  onClick={() => onStateClick(stateCode)}
                  style={{
                    default: {
                      fill,
                      stroke: strokeColor,
                      strokeWidth,
                      outline: "none",
                      opacity,
                      cursor: "pointer",
                      transition: "all 0.25s ease",
                      filter: filter || 'none',
                    },
                    hover: {
                      fill: isSelected
                        ? (isAhp ? 'var(--civic-gold)' : 'hsl(var(--primary))')
                        : (isAhp ? 'var(--civic-red)' : 'hsl(var(--primary))'),
                      stroke: isAhp ? 'var(--civic-cream)' : 'hsl(var(--border))',
                      strokeWidth: isSelected ? 1.5 : 1,
                      outline: "none",
                      opacity: 1,
                      cursor: "pointer",
                      filter: isSelected ? 'url(#state-glow)' : 'none',
                    },
                    pressed: {
                      fill: isAhp ? 'var(--civic-gold)' : 'hsl(var(--primary))',
                      stroke: isAhp ? 'var(--civic-cream)' : 'hsl(var(--border))',
                      strokeWidth: 1.5,
                      outline: "none",
                      opacity: 1,
                      cursor: "pointer",
                    },
                  }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>
      {(hoveredState || (hasSelection && selectedState)) && (
        <div className={`text-center text-xs mt-1 ${
          isAhp ? 'text-civic-cream' : 'text-foreground'
        }`}>
          {hoveredState || selectedState}
        </div>
      )}
    </div>
  );
};
